<template>
  <div class="box_legal">
    <div
      class="condition"
      tabindex="0"
      @blur="closeDropdown"
      @click="toggleDropdown"
    >
      <div class="item_selected" :class="{ open: isOpenMoDropdown }">
        <span>
          {{ selectedTab.name }}
        </span>
        <atoms-icons-icon-base
          class="icon_selected"
          view-box="0 0 24 24"
          width="24px"
          height="24px"
        >
          <atoms-icons-icon-chevron />
        </atoms-icons-icon-base>
      </div>
      <div class="box_item" :class="{ open: isOpenMoDropdown }">
        <div
          v-for="(tab, index) in tabItems"
          :key="index"
          class="item_condition"
          :class="{
            active: tab.id === selectedTab.id
          }"
          @click="onClickItem(tab.id)"
        >
          {{ tab.name }}
        </div>
      </div>
    </div>
    <div class="contents">
      <client-only>
        <div v-if="showPending" class="toy_thread_contents">
          <atoms-loading-spinner class="spinner" />
        </div>

        <div
          v-else-if="status === 'success'"
          v-dompurify-html="data?.contents"
        ></div>
      </client-only>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useDelayedPending } from '@/composables/useDelayedPending'
type LegalPageType = 'term' | 'operation'
definePageMeta({
  validate: async (route) => {
    return ['operation', 'term'].includes(route.params.type.toString())
  }
})

const TAB_NAME_BY_TYPE = {
  term: [
    {
      id: 925,
      name: '헬로메이플 이용약관'
    },
    {
      id: 932,
      name: '개인정보처리방침'
    },
    {
      id: 934,
      name: '플랫폼 IP사용 가이드'
    }
  ],
  operation: [
    {
      id: 933,
      name: '청소년보호정책'
    },
    {
      id: 935,
      name: '플랫폼 이용 가이드라인'
    },
    {
      id: 1020,
      name: '이벤트 규약'
    }
  ]
}

const { $api } = useNuxtApp()
const config = useRuntimeConfig()
const route = useRoute()
const isOpenMoDropdown = ref(false)
const tabItems = computed(() => {
  return TAB_NAME_BY_TYPE[route.params.type as LegalPageType]
})
const selectedTab = computed(() => {
  const queryId = Number(route.query.id)
  const currentTab = tabItems.value.find((item) => {
    return item.id === queryId
  })

  if (currentTab) {
    return currentTab
  }
  return tabItems.value[0]
})

useSeoMeta({
  title: () => selectedTab.value.name,
  ogTitle: () => selectedTab.value.name,
  twitterTitle: () => selectedTab.value.name,
  ogUrl: () => `${config.public.baseURL}/${route.fullPath}`
})

// 운영정책 저장 데이터
const storedData = ref<{ [key: string]: any }>({})

const { data, error, status } = await useAsyncData(
  () => {
    if (storedData.value[selectedTab.value.id]) {
      // 기존 저장된 데이터가 있으면 저장된 데이터 반환
      return new Promise<TermResponse>((resolve) =>
        resolve({ code: 0, data: storedData.value[selectedTab.value.id] })
      )
    }
    return $api.term.getTerm(selectedTab.value.id)
  },
  {
    watch: [selectedTab],
    transform(res: { code: number; data: any }) {
      if (res.code === 0) {
        storedData.value[selectedTab.value.id] = res.data
        return res.data
      }
      return {} as Term
    }
  }
)
const showPending = useDelayedPending(status, 500)

if (error.value) {
  throw error
}
/**
 * 모바일 드롭다운 메뉴 닫기
 */
function closeDropdown() {
  console.log('blur')
  isOpenMoDropdown.value = false
}

/**
 * 모바일 드롭다운 오픈 여부 변경
 */
function toggleDropdown() {
  isOpenMoDropdown.value = !isOpenMoDropdown.value
}

/**
 * 탭/드롭다운 아이템 클릭 이벤트
 * @param id
 */
function onClickItem(id: number) {
  useRouter().push({
    name: 'legal-type',
    query: {
      id
    }
  })
}
</script>

<style scoped lang="scss">
.box_legal {
  margin-top: 40px;
  .condition {
    .item_selected {
      display: none;
    }
    .box_item {
      display: flex;
      .item_condition {
        cursor: pointer;
        width: 240px;
        height: 72px;
        border-radius: 8px 8px 0 0;
        background-color: #e1e2ef;
        color: rgba(0, 0, 0, 0.2);
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.5;
        letter-spacing: -0.8px;
        &.active {
          background-color: #fff;
          color: $black0;
        }
      }
    }
  }
  .contents {
    min-height: 1300px;
    padding: 40px;
    background-color: #ffffff;
    border-radius: 0 8px 8px 8px;
    text-align: initial;
    .spinner {
      padding-top: 100px;
    }
  }
}
@media (max-width: 999px) {
  .box_legal {
    margin-top: 0;
    .condition {
      position: relative;
      z-index: 1;
      .item_selected {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 56px;
        background-color: #fff;
        box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.06);
        color: $black0;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.5;
        letter-spacing: -0.64px;
        position: relative;
        .icon_selected {
          position: absolute;
          right: 16px;
          color: $gray1;
        }
        &.open {
          .icon_selected {
            transform: rotate(180deg);
          }
        }
      }
      .box_item {
        display: none;
        width: 100%;
        box-shadow: 0 8px 16px -8px rgba(0, 0, 0, 0.06);
        border-top: 1px solid #dfe4ea;
        &.open {
          display: block;
          position: absolute;
        }
        .item_condition {
          z-index: 1;
          width: 100%;
          height: 56px;
          color: $black0;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.5;
          background-color: #ffffff;
          letter-spacing: -0.64px;
          &.active {
            font-weight: 700;
          }
          &:hover {
            font-weight: 700;
            color: $blue1;
          }
        }
      }
    }
    .contents {
      margin: 24px 16px 0;
      border-radius: 8px;
    }
  }
}
</style>
